import type { TradeProAction } from './actions';
import { log } from './log';
import { createLeg, OrderAction } from './types';
import type {
    TradeProState,
    TradeProSingleLegState,
    TradeProMultiLegState,
    OrderData,
    MultiLegOrderData,
    OptionLeg,
    EquityLeg,
} from './types';
import {
    ORDER_CORE_PROPS,
    isMultiLegState,
    newSymbolPickerStateFromExistingSymbol,
    getExDestinationAndRouteFromSchemaDefaults,
    calculateOrderPrices,
    getSelectedExecutorSchema,
    getSelectedRoute,
    setSchemaAndUISchemaAfterOrderDataChange,
    setSchemaChangesAfterInstrumentChange,
    recalculateLegQuantity,
    getBasicExecutorsSchema,
    handleFetchedExecutorSchema,
    generateSchemaForState,
    filterOrderDataBySchema,
    getDefaultOrderDataFromSelectedExecutorSchema,
} from './utils';
import type { Layout } from '@jsonforms/core';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { isEqual } from 'es-toolkit';
import {
    defaultQuantityValueByInstrumentType,
    defaultQuantityValueMultiLeg,
    defaultQuantityStepMultiLeg,
} from 'src/components/JsonForm/ThinkalphaTheme';
import {
    setUnderlyingSymbol,
    createInitialState as createSymbolPickerInitialState,
} from 'src/features/symbol-picker/state';
import { symbolPickerReducer } from 'src/features/symbol-picker/state/reducer';
import { getUnderlyingSymbolFromInstrument, instrumentToString } from 'src/lib/instrumentUtils';
import { getGtdTimeForDate } from 'src/lib/orders';
import { removeInstantPropsFromOrderData } from 'src/lib/orders/removePropsFromOrderData';
import { DEFAULT_ROUTE, DEFAULT_EXDESTINATION } from 'src/models/TradeProWidgetModel';

/**
 * Custom cloning function that preserves Leg class instances.
 * structuredClone loses the prototype chain, converting Leg instances to plain objects.
 * This function uses structuredClone for the base data but reconstructs Leg instances.
 */
export function cloneOrderData<T extends OrderData>(orderData: T): T {
    const cloned = structuredClone(orderData);

    // If this is multi-leg order data with legs, reconstruct the Leg instances
    if ('legs' in cloned && Array.isArray(cloned.legs)) {
        const multiLegCloned = cloned as unknown as MultiLegOrderData;
        multiLegCloned.legs = multiLegCloned.legs.map((leg) => createLeg(leg));
    }

    return cloned;
}

export function setUnderlyingSymbolForLegs(
    state: TradeProMultiLegState,
    instrument: Instrument,
): TradeProMultiLegState {
    return {
        ...state,
        orderData: {
            ...state.orderData,
            legs: state.orderData.legs.map((leg) => {
                // Get the appropriate symbol based on leg type
                let symbolToSet: string;
                if (leg.symbolPickerState.allowedInstrumentTypes[0] === 'options') {
                    // For option legs, we need just the underlying symbol
                    symbolToSet =
                        instrument.type === 'equity'
                            ? instrument.symbol
                            : instrument.type === 'option' && instrument.underlyingInstrument.type === 'equity'
                              ? instrument.underlyingInstrument.symbol
                              : '';
                } else {
                    // For equity legs, use the full instrument string
                    symbolToSet = instrumentToString(instrument);
                }
                const newSymbolPickerState = symbolPickerReducer(
                    leg.symbolPickerState,
                    setUnderlyingSymbol(symbolToSet),
                );
                return createLeg({
                    ...leg,
                    symbolPickerState: newSymbolPickerState,
                });
            }),
        },
    };
}

// Initial state for single-leg
export const initialSingleLegState: TradeProSingleLegState = {
    type: 'single-leg',
    orderData: {
        exDestination: DEFAULT_EXDESTINATION,
        route: DEFAULT_ROUTE,
        instrument: undefined,
        account: '',
        quantity: defaultQuantityValueByInstrumentType.equity,
        type: 'market',
        duration: 'day',
        side: OrderAction.Buy,
        attributes: {},
    },
    initialOrderDataSnapshot: null,
    orderErrors: [],
    executorsSchema: getBasicExecutorsSchema(),
    executorsSchemaIsLoading: false,
    executorsSchemaFetchingError: null,
    fallbackToBasicSchema: false,
    exDestinationsWithRoutes: {},
    allowedInstrumentTypes: ['equity', 'options'],
    selectedExecutorSchema: {},
    schema: {},
    uischema: {
        type: 'GridLayout',
        elements: [],
    } as Layout,
    showTradeToast: true,
    skipTradeConfirmation: false,
    shouldPopulateDefaultPrice: false,
    defaultPriceSource: 'last',
    marketData: {
        bid: undefined,
        ask: undefined,
        last: undefined,
    },
    selectedAccountEffectiveAccess: 'reader',
    hasCompletedInitialSchemaFetch: false,
    lastRawExecutorSchema: undefined,
};

export const createInitialMultiLegLegs = (underlyingSymbolInstrument?: Instrument): (OptionLeg | EquityLeg)[] => {
    function newSymbolPickerStateFromExistingSymbol(underlyingSymbolInstrument: Instrument | undefined) {
        let symbolPickerState = createSymbolPickerInitialState('', ['options']);
        if (underlyingSymbolInstrument) {
            const underlyingSymbol = getUnderlyingSymbolFromInstrument(underlyingSymbolInstrument);
            symbolPickerState = symbolPickerReducer(symbolPickerState, setUnderlyingSymbol(underlyingSymbol));
        }
        return symbolPickerState;
    }

    return [
        createLeg({
            symbolPickerState: newSymbolPickerStateFromExistingSymbol(underlyingSymbolInstrument),
            side: OrderAction.Buy,
            quantity: 1,
            quantityRatio: 1,
        }),
        createLeg({
            symbolPickerState: newSymbolPickerStateFromExistingSymbol(underlyingSymbolInstrument),
            side: OrderAction.Sell,
            quantity: 1,
            quantityRatio: 1,
        }),
    ];
};

// Initial state for multi-leg
export const initialMultiLegState: TradeProMultiLegState = {
    type: 'multi-leg',
    orderData: {
        exDestination: DEFAULT_EXDESTINATION,
        route: DEFAULT_ROUTE,
        instrument: undefined,
        account: '',
        quantity: defaultQuantityValueMultiLeg, // Will be calculated from legs
        type: 'market',
        duration: 'day',
        side: OrderAction.MultiLeg,
        attributes: {},
        legs: createInitialMultiLegLegs(),
    },
    initialOrderDataSnapshot: null,
    activeStrategy: 'custom',
    orderErrors: [],
    executorsSchema: getBasicExecutorsSchema(),
    executorsSchemaIsLoading: false,
    executorsSchemaFetchingError: null,
    fallbackToBasicSchema: false,
    exDestinationsWithRoutes: {},
    allowedInstrumentTypes: ['equity'],
    selectedExecutorSchema: {},
    schema: {},
    uischema: {
        type: 'GridLayout',
        elements: [],
    } as Layout,
    showTradeToast: true,
    skipTradeConfirmation: false,
    shouldPopulateDefaultPrice: false,
    defaultPriceSource: 'last',
    marketData: {
        bid: undefined,
        ask: undefined,
        last: undefined,
    },
    selectedAccountEffectiveAccess: 'reader',
    hasCompletedInitialSchemaFetch: false,
    lastRawExecutorSchema: undefined,
};

// Default to single-leg for backward compatibility
export const initialState: TradeProState = initialSingleLegState;

export function tradeProReducer(state: TradeProState, action: TradeProAction): TradeProState {
    if (TRACE) {
        log.trace({
            message: `tradeProReducer action ${action.type}`,
            // Uncommenting this WILL cause performance issues due to large action objects
            /* action */
        });
    }

    // Multi-leg specific actions
    if (
        ['ADD_LEG', 'UPDATE_LEG', 'REMOVE_LEG', 'SET_ACTIVE_STRATEGY', 'SET_LEGS', 'UPDATE_LEG_SYMBOL_PICKER'].includes(
            action.type,
        )
    ) {
        if (!isMultiLegState(state)) {
            log.error('Multi-leg action dispatched on single-leg state');
            return state;
        }

        switch (action.type) {
            case 'ADD_LEG': {
                const symbolPickerState = newSymbolPickerStateFromExistingSymbol(
                    state.orderData.instrument,
                    action.legType,
                );

                const legWithSymbolPicker = createLeg({
                    ...action.leg,
                    symbolPickerState,
                });

                const newLeg = recalculateLegQuantity(legWithSymbolPicker, state.orderData.quantity);

                return {
                    ...state,
                    orderData: {
                        ...state.orderData,
                        legs: [...state.orderData.legs, newLeg],
                    },
                    orderErrors: [],
                };
            }

            case 'UPDATE_LEG':
                return {
                    ...state,
                    orderData: {
                        ...state.orderData,
                        legs: state.orderData.legs.map((leg) => {
                            if (leg.id === action.legId) {
                                const updatedLeg = createLeg({ ...leg, ...action.updates });
                                return recalculateLegQuantity(updatedLeg, state.orderData.quantity);
                            }
                            return leg;
                        }),
                    },
                };

            case 'REMOVE_LEG':
                return {
                    ...state,
                    orderData: {
                        ...state.orderData,
                        legs: state.orderData.legs.filter((leg) => leg.id !== action.legId),
                    },
                    orderErrors: [],
                };

            case 'SET_ACTIVE_STRATEGY':
                return {
                    ...state,
                    activeStrategy: action.strategy,
                };

            case 'SET_LEGS':
                return {
                    ...state,
                    orderData: {
                        ...state.orderData,
                        legs: action.legs,
                    },
                    orderErrors: [],
                };

            case 'UPDATE_LEG_SYMBOL_PICKER': {
                return {
                    ...state,
                    orderData: {
                        ...state.orderData,
                        legs: state.orderData.legs.map((leg) => {
                            if (leg.id !== action.legId) return leg;

                            const newSymbolPickerState = symbolPickerReducer(
                                leg.symbolPickerState,
                                action.symbolPickerAction,
                            );

                            // Reset quantityRatio to default when instrument type changes
                            let newQuantityRatio = leg.quantityRatio;
                            if (action.symbolPickerAction.type === 'SET_INSTRUMENT_TYPE') {
                                const oldType = leg.symbolPickerState.instrument.instrumentType;
                                const newType = newSymbolPickerState.instrument.instrumentType;
                                if (oldType !== newType) {
                                    newQuantityRatio = newType === 'equity' ? 100 : 1;
                                }
                            }

                            const newLeg = createLeg({
                                ...leg,
                                symbolPickerState: newSymbolPickerState,
                                quantityRatio: newQuantityRatio,
                            });

                            return recalculateLegQuantity(newLeg, state.orderData.quantity);
                        }),
                    },
                };
            }
        }
    }

    switch (
        action.type // Handle initialization
    ) {
        case 'INITIALIZE_STATE': {
            const initialState =
                action.payload.stateType === 'multi-leg' ? initialMultiLegState : initialSingleLegState;

            const orderData = {
                ...initialState.orderData,
                instrument: action.payload.instrument,
                account: action.payload.account,
            };

            return {
                ...initialState,
                orderData,
                showTradeToast: action.payload.showTradeToast,
                skipTradeConfirmation: action.payload.skipTradeConfirmation,
                initialOrderDataSnapshot: cloneOrderData(orderData),
            } as TradeProState;
        }

        case 'START_FETCHING_EXECUTOR_SCHEMA': {
            return {
                ...state,
                executorsSchemaIsLoading: true,
                fallbackToBasicSchema: false,
            };
        }

        case 'FETCHED_EXECUTOR_SCHEMA': {
            // Skip processing if the raw schema reference is unchanged (structuralSharing
            // preserves the same reference for identical data on background refetches)
            if (action.schema === state.lastRawExecutorSchema) {
                // Defensively clear transient flags even though in practice
                // this path only runs on background refetches where they're already clean
                if (state.executorsSchemaIsLoading || state.executorsSchemaFetchingError !== null) {
                    return {
                        ...state,
                        executorsSchemaIsLoading: false,
                        executorsSchemaFetchingError: null,
                    };
                }
                return state;
            }

            const newState = handleFetchedExecutorSchema(
                state,
                action.schema,
                action.accountId,
                state.hasCompletedInitialSchemaFetch,
            );
            return {
                ...newState,
                hasCompletedInitialSchemaFetch: true,
                lastRawExecutorSchema: action.schema,
            };
        }

        case 'FAILED_FETCHING_EXECUTOR_SCHEMA': {
            return {
                ...state,
                executorsSchemaIsLoading: false,
                executorsSchemaFetchingError: action.error,
                executorsSchema: {},
                lastRawExecutorSchema: undefined,
            };
        }

        case 'FALLBACK_TO_BASIC_SCHEMA': {
            return {
                ...handleFetchedExecutorSchema(state, getBasicExecutorsSchema()),
                fallbackToBasicSchema: true,
                lastRawExecutorSchema: undefined,
            };
        }

        case 'SET_EXDESTINATION_AND_ROUTE_FROM_BASE_SCHEMA_DEFAULTS': {
            const { exDestination, route } = getExDestinationAndRouteFromSchemaDefaults(state.executorsSchema);

            const selectedExecutorSchema = getSelectedExecutorSchema(
                state.executorsSchema,
                exDestination || state.orderData.exDestination,
            );

            const newOrderData = {
                ...state.orderData,
            };

            if (exDestination) {
                newOrderData.exDestination = exDestination;
            }
            newOrderData.route = route;

            return setSchemaAndUISchemaAfterOrderDataChange({
                ...state,
                orderData: newOrderData,
                executorsSchemaIsLoading: false,
                executorsSchemaFetchingError: null,
                selectedExecutorSchema,
            } as TradeProState);
        }

        case 'RESET_ORDER_DATA': {
            if (!state.initialOrderDataSnapshot) {
                return state; // No snapshot to reset to
            }

            let newOrderData = cloneOrderData(state.initialOrderDataSnapshot);

            if (action.orderData) {
                newOrderData = {
                    ...newOrderData,
                    ...action.orderData,
                };
            }

            if (isMultiLegState(state)) {
                const newState = {
                    ...state,
                    shouldPopulateDefaultPrice: true,
                    orderData: {
                        ...newOrderData,
                        legs: createInitialMultiLegLegs(newOrderData.instrument),
                    },
                    orderErrors: [],
                    activeStrategy: 'custom',
                } as TradeProState;

                return newState;
            }

            return {
                ...state,
                shouldPopulateDefaultPrice: true,
                orderData: newOrderData,
                orderErrors: [],
            } as TradeProState;
        }

        case 'SET_SHOW_TRADE_TOAST': {
            return {
                ...state,
                showTradeToast: action.value,
            };
        }

        case 'SET_SKIP_TRADE_CONFIRMATION': {
            return {
                ...state,
                skipTradeConfirmation: action.value,
            };
        }

        case 'SET_INSTRUMENT': {
            // When setting a new instrument, we need to ensure that the price and stopPrice are cleared
            const { price: _price, stopPrice: _stopPrice, ...oldState } = state.orderData;
            let instrument = action.value;

            if (instrument.type === 'option') {
                instrument = {
                    ...instrument,
                    expiry: instrument.expiry.toString() as any, // PLAT-10449
                };
            }

            // For multi-leg widgets, the main orderData.instrument must always be an equity instrument
            // representing the underlying. If an option instrument is provided, extract its underlying.
            let instr = instrument;
            if (isMultiLegState(state) && instrument.type === 'option') {
                if (instrument.underlyingInstrument.type === 'equity') {
                    instr = instrument.underlyingInstrument;
                } else {
                    log.warn('Multi-leg widget received option with non-equity underlying, using original instrument');
                }
            }

            const changes = setSchemaChangesAfterInstrumentChange(
                state.executorsSchema,
                instr,
                state.orderData.exDestination,
                state.orderData.route,
                isMultiLegState(state),
            );

            const newState = {
                ...state,
                shouldPopulateDefaultPrice: true,
                exDestinationsWithRoutes: changes.exDestinationsWithRoutes,
                allowedInstrumentTypes: changes.allowedInstrumentTypes,
                selectedExecutorSchema: changes.selectedExecutorSchema,
                orderData: {
                    ...oldState,
                    quantity:
                        oldState.side === 'multi-leg'
                            ? defaultQuantityStepMultiLeg
                            : (action.quantity ?? defaultQuantityValueByInstrumentType[instr.type]),
                    instrument: instr,
                    exDestination: changes.exDestination ?? state.orderData.exDestination,
                    route: changes.route,
                },
            };

            if (instr.type !== 'option') {
                if ('openClose' in newState.orderData) {
                    delete newState.orderData.openClose;
                }
            }

            // Replace the instrument for each Leg in multi-leg state
            if (isMultiLegState(newState as TradeProState)) {
                const newMultiLegState = setUnderlyingSymbolForLegs(newState as TradeProMultiLegState, instrument);
                return setSchemaAndUISchemaAfterOrderDataChange(newMultiLegState);
            }

            return setSchemaAndUISchemaAfterOrderDataChange(newState as TradeProState);
        }

        case 'SET_ROUTE': {
            return {
                ...state,
                orderData: {
                    ...state.orderData,
                    route: action.value,
                },
            } as TradeProState;
        }

        case 'SET_PRICE': {
            return {
                ...state,
                orderData: {
                    ...state.orderData,
                    price: Number(action.value.replace(/_/g, '')),
                },
                shouldPopulateDefaultPrice: false,
            } as TradeProState;
        }

        case 'SET_STOP_PRICE': {
            return {
                ...state,
                orderData: {
                    ...state.orderData,
                    stopPrice: Number(action.value.replace(/_/g, '')),
                },
                shouldPopulateDefaultPrice: false,
            } as TradeProState;
        }

        case 'SET_STOP_LIMIT_PRICE': {
            return {
                ...state,
                orderData: {
                    ...state.orderData,
                    stopPrice: Number(action.stop.replace(/_/g, '')),
                    price: Number(action.limit.replace(/_/g, '')),
                },
                shouldPopulateDefaultPrice: false,
            } as TradeProState;
        }

        case 'SET_PRICE_BY_CLICKING_A_TICKER': {
            if (action.value === undefined) {
                return state;
            }

            const type = state.orderData.type === 'market' || !state.orderData.type ? 'limit' : state.orderData.type;
            const prices = calculateOrderPrices(action.ticker, action.value, type);

            return setSchemaAndUISchemaAfterOrderDataChange({
                ...state,
                shouldPopulateDefaultPrice: false, // we just populated the price, so we don't want a market data tic to update it again
                orderData: {
                    ...state.orderData,
                    ...prices,
                    type,
                },
            } as TradeProState);
        }

        case 'SET_MARKET_DATA': {
            const bid = action.payload?.bid === undefined ? state.marketData.bid : action.payload?.bid;
            const ask = action.payload?.ask === undefined ? state.marketData.ask : action.payload?.ask;
            const last = action.payload?.last === undefined ? state.marketData.last : action.payload?.last;

            // Sometimes updates come as NaNs, which ends up in an infinite loop of updates
            const skipUpdate =
                (Number.isNaN(bid) || state.marketData.bid === bid) &&
                (Number.isNaN(ask) || state.marketData.ask === ask) &&
                (Number.isNaN(last) || state.marketData.last === last);

            if (skipUpdate) {
                return state;
            }

            let newState = {
                ...state,
                marketData: {
                    bid,
                    ask,
                    last,
                },
            };

            /**
             * Setting of price(s) logic
             */
            let defaultPrice: number | undefined;
            if (newState.defaultPriceSource === 'mid') {
                // Calculate mid price from bid and ask
                const bid = newState.marketData.bid;
                const ask = newState.marketData.ask;
                if (bid !== undefined && ask !== undefined) {
                    defaultPrice = (bid + ask) / 2;
                }
            } else {
                defaultPrice = newState.marketData[newState.defaultPriceSource];
            }

            if (
                newState.shouldPopulateDefaultPrice &&
                defaultPrice !== undefined &&
                newState.orderData.type !== 'market'
            ) {
                const prices = calculateOrderPrices(newState.defaultPriceSource, defaultPrice, newState.orderData.type);

                newState = {
                    ...newState,
                    shouldPopulateDefaultPrice: false,
                    orderData: {
                        ...newState.orderData,
                        ...prices,
                    },
                } as TradeProState;
            }

            return newState as TradeProSingleLegState;
        }

        case 'SET_SELECTED_ACCOUNT_EFFECTIVE_ACCESS': {
            if (state.selectedAccountEffectiveAccess === action.permission) {
                return state;
            }

            return setSchemaAndUISchemaAfterOrderDataChange({
                ...state,
                selectedAccountEffectiveAccess: action.permission,
            });
        }

        case 'PATCH_ORDER_DATA': {
            const oldData = state.orderData;
            const newData = action.value;

            // This prevents unnecessary json-form rerenders / infinite loops
            if (isEqual(oldData, newData)) {
                return state;
            }

            const newState = {
                ...state,
                hasCompletedInitialSchemaFetch:
                    'account' in newData && newData.account !== oldData.account
                        ? false
                        : state.hasCompletedInitialSchemaFetch,
            };

            let localData = { ...oldData, ...newData };

            let cleanData: OrderData = {
                exDestination: newData.exDestination || oldData.exDestination,
                route: newData.route || oldData.route,
                instrument: newData.instrument || oldData.instrument,
                account: newData.account || oldData.account,
                side: oldData.side,
                type: newData.type || oldData.type,
            };

            // Recalculate executors related state if the instrument has changed
            if (newData.instrument && !isEqual(oldData.instrument, newData.instrument)) {
                const changes = setSchemaChangesAfterInstrumentChange(
                    state.executorsSchema,
                    newData.instrument,
                    localData.exDestination,
                    localData.route,
                    isMultiLegState(state),
                );
                newState.exDestinationsWithRoutes = changes.exDestinationsWithRoutes;
                newState.allowedInstrumentTypes = changes.allowedInstrumentTypes;
                newState.selectedExecutorSchema = changes.selectedExecutorSchema;
                localData.exDestination = changes.exDestination ?? cleanData.exDestination;
                localData.route = changes.route;

                if (TRACE) {
                    log.trace(
                        'recalculate exDestinationsWithRoutes, selectedExecutorSchema exDestination and route with the instrument has changed',
                    );
                }
            }

            // Remove price and stopPrice if the instrument has changed
            if ('instrument' in newData && !isEqual(oldData.instrument, newData.instrument)) {
                const { price: _price, stopPrice: _stopPrice, ...rest } = localData;
                localData = rest;

                if (TRACE) {
                    log.trace('removed price and stopPrice due to instrument change');
                }
            }
            // Remove price and stopPrice if the order type is market
            if (localData.type === 'market' && (localData.price || localData.stopPrice)) {
                const { price: _price, stopPrice: _stopPrice, ...rest } = localData;
                localData = rest;

                if (TRACE) {
                    log.trace('removed price and stopPrice due to new order type market');
                }
            }
            // Remove stopPrice if the order type is limit
            if (localData.type === 'limit' && localData.stopPrice) {
                const { stopPrice: _stopPrice, ...rest } = localData;
                localData = rest;

                if (TRACE) {
                    log.trace('removed stopPrice due to order type === limit');
                }
            }
            // Remove price if the order type is stop
            if (localData.type === 'stop' && localData.price) {
                const { price: _price, ...rest } = localData;
                localData = rest;

                if (TRACE) {
                    log.trace('removed price due to order type === stop');
                }
            }
            // Remove expireTime if duration is not GTD anymore
            if (
                oldData?.duration === 'gtd' &&
                'duration' in newData &&
                newData?.duration !== 'gtd' &&
                'expireTime' in localData
            ) {
                const { expireTime: _expireTime, ...rest } = localData;
                localData = rest;

                if (TRACE) {
                    log.trace('removed expireTime due to duration !== GTD');
                }
            }
            if (localData.duration === 'gtd' && 'expireTime' in localData) {
                localData.expireTime = getGtdTimeForDate(localData.expireTime);

                if (TRACE) {
                    log.trace('altered expireTime due to duration === GTD');
                }
            }
            // Replace FOK with DAY for duration if order type is changed to market
            if (oldData?.type !== 'market' && newData?.type === 'market' && localData.duration === 'fok') {
                localData.duration = 'day';

                if (TRACE) {
                    log.trace('replace FOK with DAY for duration for market order');
                }
            }

            /**
             * If we change exDestination we need to recalculate the selectedExecutorSchema and route
             */
            let routeWasRecalculated = false;
            if (oldData.exDestination !== localData.exDestination) {
                const { route: routeDefault } = getExDestinationAndRouteFromSchemaDefaults(state.executorsSchema);

                newState.selectedExecutorSchema = getSelectedExecutorSchema(
                    newState.executorsSchema,
                    localData.exDestination,
                );

                // Use filtered routes from exDestinationsWithRoutes for route selection
                const availableRoutes = newState.exDestinationsWithRoutes[localData.exDestination] || [DEFAULT_ROUTE];
                cleanData.route = getSelectedRoute(availableRoutes, routeDefault ?? localData.route ?? DEFAULT_ROUTE);
                routeWasRecalculated = true;
            }

            /**
             * If we change exDestination, route, or account, we need to clean up the state as extra properties will
             * end up in form validation errors due to JSON schema validation rejecting extra properties.
             * We first recalculate the new schema, then filter properties based on it.
             *
             * Note: When account changes, we filter based on the current executorsSchema in state.
             * This schema might be stale (for the old account) if the new account's schema hasn't been
             * fetched yet. However, we MUST filter here because if TanStack Query serves the schema from
             * cache (switching back to a previously used account), FETCHED_EXECUTOR_SCHEMA won't be
             * dispatched again, so this is our only chance to filter. FETCHED_EXECUTOR_SCHEMA will
             * re-filter if it does get dispatched with a fresh schema.
             */
            if (
                ('exDestination' in newData || 'route' in newData || 'account' in newData) &&
                (oldData.exDestination !== localData.exDestination ||
                    oldData.route !== localData.route ||
                    oldData.account !== localData.account)
            ) {
                /**
                 * If we change exDestination we need to recalculate the selectedExecutorSchema and route
                 * IMPORTANT: This must happen BEFORE generating the schema for cleanup
                 */
                if (oldData.exDestination !== localData.exDestination) {
                    const { route: routeDefault } = getExDestinationAndRouteFromSchemaDefaults(state.executorsSchema);

                    newState.selectedExecutorSchema = getSelectedExecutorSchema(
                        newState.executorsSchema,
                        localData.exDestination,
                    );

                    // Use filtered routes from exDestinationsWithRoutes for route selection
                    const availableRoutes = newState.exDestinationsWithRoutes[localData.exDestination] || [
                        DEFAULT_ROUTE,
                    ];
                    localData.route = getSelectedRoute(
                        availableRoutes,
                        routeDefault ?? localData.route ?? DEFAULT_ROUTE,
                    );
                } else {
                    // Even if exDestination didn't change, ensure selectedExecutorSchema is set
                    // This handles the case when route changes but exDestination stays the same
                    // OR when neither changed (in which case we use the existing one)
                    newState.selectedExecutorSchema = getSelectedExecutorSchema(
                        newState.executorsSchema,
                        localData.exDestination,
                    );
                }

                const tempSchema = generateSchemaForState({
                    ...newState,
                    orderData: {
                        ...cleanData,
                        ...localData,
                    },
                } as TradeProState);

                // Use the utility function to filter orderData based on the new schema
                // This removes extra properties that don't exist in the new schema
                cleanData = filterOrderDataBySchema(localData, tempSchema);

                if (TRACE) {
                    log.trace({
                        message: 'changed exDestination, route, or account - filtered properties based on schema',
                        cleanedProperties: Object.keys(cleanData),
                    });
                }

                // Clean up properties against the schema (removes properties not in schema)
                const mergedData = { ...cleanData, ...localData };
                if (routeWasRecalculated) {
                    mergedData.route = cleanData.route;
                }
                cleanData = filterOrderDataBySchema(mergedData, tempSchema) as typeof cleanData;

                // Extract and apply default values from the new schema
                const schemaDefaults = getDefaultOrderDataFromSelectedExecutorSchema(
                    newState.selectedExecutorSchema,
                    cleanData.route,
                );

                // Apply default values from the new schema, overwriting existing values
                for (const [key, defaultValue] of Object.entries(schemaDefaults)) {
                    // Core properties should not be overwritten by defaults
                    if (!(ORDER_CORE_PROPS as readonly string[]).includes(key)) {
                        cleanData[key] = defaultValue;

                        if (TRACE) {
                            log.trace({
                                message: `applied default value for '${key}' from new schema`,
                                defaultValue,
                            });
                        }
                    }
                }

                // Store the pre-calculated schema for route selection
                // Final schema generation with cleanup will happen at the end
                newState.schema = tempSchema;
            } else {
                cleanData = {
                    ...cleanData,
                    ...localData,
                };
            }

            /**
             * Recalculate the quantity of all legs in multi-leg if quantity is changed
             */
            if (isMultiLegState(newState) && 'quantity' in newData) {
                cleanData.legs = newState.orderData.legs.map((leg) => recalculateLegQuantity(leg, newData.quantity!));
            } else if (isMultiLegState(newState)) {
                cleanData.legs = newState.orderData.legs;
            }

            // Update orderData with the cleaned data
            newState.orderData = removeInstantPropsFromOrderData(cleanData);
            newState.shouldPopulateDefaultPrice = false;

            // Generate schema and UI schema
            const stateWithSchema = setSchemaAndUISchemaAfterOrderDataChange(newState as TradeProState);

            // Clean up orderData against the new schema when exDestination, route, or account changes
            // This removes properties not in schema to prevent validation errors
            if (
                ('exDestination' in newData || 'route' in newData || 'account' in newData) &&
                (oldData.exDestination !== localData.exDestination ||
                    oldData.route !== localData.route ||
                    oldData.account !== localData.account)
            ) {
                stateWithSchema.orderData = filterOrderDataBySchema(stateWithSchema.orderData, stateWithSchema.schema);
            }

            return stateWithSchema;
        }

        case 'SET_ORDER_ERRORS': {
            return {
                ...state,
                orderErrors: action.value,
            };
        }

        default:
            return state;
    }
}
